<template>
    <div
        class="font-industry relative flex h-full w-full flex-col p-4"
        :class="{
            'bg-black opacity-50': !isCurrentTurn,
        }"
    >
        <div
            class="flex items-center space-x-2"
            :class="{
                'justify-start': !isSoloGame,
                'justify-center': isSoloGame,
            }"
        >
            <Avatar :src="playerAvatar" :size="12" />
            <div class="flex flex-col">
                <div class="line-clamp-1 max-w-20 truncate font-bold text-white">
                    {{ playerName }}
                </div>
                <div
                    class="w-28 text-xs font-semibold text-green-500"
                    :class="average ? '' : 'opacity-0'"
                >
                    {{ averageLabel }}: {{ average }}
                </div>
            </div>
        </div>

        <div
            class="text-titles mt-4 flex items-center text-sm font-bold uppercase"
            :class="{
                'justify-between': !isSoloGame,
                'justify-center space-x-10': isSoloGame,
            }"
        >
            <div v-if="showSets">
                <span class="mr-1 tracking-wider">{{ $t('play_player.sets') }}</span>
                <span class="font-bold">{{ playerSetsWon }}</span>
            </div>
            <div>
                <span class="mr-1 tracking-wider">{{ $t('play_player.legs') }}</span>
                <span class="font-bold">{{ playerLegsWon }}</span>
            </div>
        </div>

        <div
            v-if="isX01Game && remainingScore !== null && remainingScore !== undefined"
            class="mt-3 flex flex-1 flex-col"
        >
            <div class="relative flex items-center justify-center gap-4">
                <div
                    class="text-titles absolute left-0 text-sm font-bold"
                    v-if="lastThrows.length > 0"
                >
                    {{ dartsThrown }}
                    <br /><span class="text-xs">{{ $t('play_player.darts') }}</span>
                </div>
                <div class="text-5xl font-bold text-white" data-testid="remaining-score">
                    {{ remainingScore }}
                </div>
                <div
                    v-if="lastThrows.length > 0"
                    class="absolute right-0 bottom-0 flex flex-col items-end"
                >
                    <span class="text-titles text-sm font-normal">{{
                        lastThrows[lastThrows.length - 1].score
                    }}</span>
                    <span class="text-titles text-sm font-bold">{{
                        lastThrows[lastThrows.length - 1].remaining_score +
                        lastThrows[lastThrows.length - 1].score
                    }}</span>
                </div>
            </div>

            <div class="relative mt-4 flex-1 shrink-0 overflow-hidden">
                <div
                    v-if="
                        !matchStore.isMatchOver &&
                        !matchStore.isRealtimePaused &&
                        matchStore.botThrowIndicator.visible &&
                        String(matchStore.botThrowIndicator.teamId) === String(player.team)
                    "
                    class="pointer-events-none absolute top-0 left-1/2 z-40 -translate-x-1/2"
                >
                    <div
                        class="border-stroke-1 w-44 rounded-md border bg-black/75 px-3 py-2 shadow-2xl backdrop-blur-sm"
                    >
                        <div class="flex items-center gap-2">
                            <svg
                                class="h-4 w-4 animate-spin text-red-500"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    class="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    stroke-width="4"
                                />
                                <path
                                    class="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                            <span class="text-xs font-bold tracking-wider text-white uppercase">
                                {{ $t('play_player.dartbot_throwing') }}
                            </span>
                        </div>
                        <div class="mt-1 text-xs text-white/80">
                            {{ $t('play_player.throw_step', { n: matchStore.botThrowIndicator.step }) }}
                        </div>
                        <div class="mt-2 flex items-center gap-1">
                            <span
                                v-for="step in 3"
                                :key="step"
                                class="h-1.5 flex-1 rounded-full transition-colors"
                                :class="
                                    step <= matchStore.botThrowIndicator.step
                                        ? 'bg-red-500'
                                        : 'bg-white/20'
                                "
                            ></span>
                        </div>
                    </div>
                </div>
                <div
                    ref="scrollContainer"
                    class="custom-scrollbar absolute h-full flex-1 w-full shrink-0 overflow-y-auto pr-2 pb-3"
                    :class="{ 'flex flex-col items-center': isSoloGame }"
                >
                    <div
                        v-for="(turn, turnIndex) in lastThrows"
                        :key="turnIndex"
                        class="-mx-2 mr-3 flex shrink-0 items-center gap-2 rounded px-2 py-2 transition-all"
                        :class="{
                            'justify-start': this.index === 0 || isSoloGame,
                            'flex-row-reverse justify-start': this.index === 1 && !isSoloGame,
                            'cursor-pointer': !isTurnFromBot(turn) && canEditThisPlayer,
                            'cursor-default': isTurnFromBot(turn) || !canEditThisPlayer,
                        }"
                        @click="handleTurnClick(turn, turnIndex)"
                    >
                        <div
                            class="font-industry font-bold text-[#797979]"
                            :class="{
                                'text-xs': turnIndex !== lastThrows.length - 1,
                                'text-sm': turnIndex === lastThrows.length - 1,
                            }"
                        >
                            {{ turn.remaining_score + turn.score }}
                        </div>
                        <div
                            class="bg-card border-stroke-1 font-industry flex items-center justify-center gap-1 border-1 text-white"
                            :class="{
                                'h-6 w-12 text-xs font-normal': turnIndex !== lastThrows.length - 1,
                                'h-8 w-[74px] text-sm font-bold':
                                    turnIndex === lastThrows.length - 1,
                                'flex-row-reverse': this.index === 1 && !isSoloGame,
                                'border !border-red-500 bg-red-900/40': matchStore.isEditingTurn(
                                    player.team,
                                    turnIndex,
                                ),
                            }"
                        >
                            <span
                                :class="{
                                    'text-[#FFC227]': turn.score === 180,
                                }"
                            >
                                {{ turn.score }}
                            </span>
                            <svg
                                v-if="
                                    turnIndex === lastThrows.length - 1 &&
                                    !isTurnFromBot(turn) &&
                                    canEditThisPlayer
                                "
                                class="h-3 w-3"
                                :class="{
                                    'text-[#FFC227]': turn.score === 180,
                                    'text-white': turn.score !== 180,
                                }"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                stroke-width="2"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                />
                            </svg>
                        </div>
                    </div>
                </div>
                <div
                    class="pointer-events-none absolute right-0 bottom-0 left-0 h-5 w-full"
                    :class="{
                        'bg-gradient-to-t from-black to-transparent': !isCurrentTurn,
                        'from-bg bg-gradient-to-t to-transparent': isCurrentTurn,
                    }"
                ></div>
                <div
                    class="pointer-events-none absolute right-0 top-0 left-0 h-5 w-full"
                    :class="{
                        'bg-gradient-to-b from-black to-transparent': !isCurrentTurn,
                        'from-bg bg-gradient-to-b to-transparent': isCurrentTurn,
                    }"
                ></div>
            </div>
        </div>

        <div v-else-if="isCricketGame" class="mt-3 text-center">
            <div class="text-5xl font-bold text-white">
                {{ cricketScore }}
            </div>
        </div>

        <div
            v-if="isX01Game"
            class=""
            :class="{
                'opacity-0': !checkoutOptions,
                'ml-10': index === 1 && !isSoloGame,
                'flex justify-center': isSoloGame,
            }"
        >
            <div class="flex flex-col">
                <div class="mb-2">{{ $t('play_player.to_finish') }}</div>
                <div class="flex min-h-8 gap-2">
                    <div
                        v-for="(dart, index) in getCheckoutDarts()"
                        :key="index"
                        class="bg-card border-stroke-1 flex h-8 w-10 items-center justify-center border-1 text-xs font-bold text-red-600"
                    >
                        {{ dart }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useMatchStore } from '@/stores/match.js'
import { useAuthStore } from '@/stores/auth.js'
import checkoutService from '@/services/checkout.js'
import { MATCH_TYPES } from '@/constants/matchTypes.js'
import Avatar from '@/components/Avatar.vue'

export default {
    name: 'PlayPlayer',
    components: { Avatar },
    props: {
        index: {
            type: Number,
            required: true,
        },
        player: {
            type: Object,
            default: null,
        },
        isBotTurn: {
            type: Boolean,
            default: false,
        },
        // Overrides whose turn is shown (e.g. the owner of a turn being edited)
        activeProfileId: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {}
    },
    computed: {
        matchStore() {
            return useMatchStore()
        },
        authStore() {
            return useAuthStore()
        },
        canEditThisPlayer() {
            if (!this.matchStore.isRemotePlay) return true
            return String(this.player?.id) === String(this.authStore.profile?.id)
        },
        isBotPlayer() {
            return this.matchStore.isBotProfile(this.player?.id)
        },
        currentMatchTypeId() {
            return this.matchStore.getMatchTypeId
        },
        isX01Game() {
            return this.currentMatchTypeId === MATCH_TYPES.X01
        },
        isCricketGame() {
            return this.currentMatchTypeId === MATCH_TYPES.CRICKET
        },
        isSoloGame() {
            return this.matchStore.getProfiles.length === 1 && !this.matchStore.isTeamPlay
        },
        playerAvatar() {
            return this.player.avatar
        },
        playerName() {
            return this.player.tag_name || this.player.name
        },
        remainingScore() {
            if (!this.isX01Game) return null
            return this.matchStore.getCompetitorRemainingScore(this.player.team)
        },
        cricketScore() {
            if (!this.isCricketGame || !this.player?.id) return 0

            const sets = this.matchData?.sets || []
            if (!sets.length) return 0

            const currentSet = sets[sets.length - 1]
            if (!currentSet?.legs?.length) return 0

            const currentLeg = currentSet.legs[currentSet.legs.length - 1]
            if (!currentLeg?.turns?.length) return 0

            const playerTurns = currentLeg.turns.filter(
                (turn) => turn.profile_id === this.player.id,
            )
            if (!playerTurns.length) return 0

            const latestTurn = playerTurns[playerTurns.length - 1]
            return latestTurn?.state?.score || 0
        },
        isCurrentTurn() {
            const activeId = this.activeProfileId ?? this.matchStore.getCurrentPlayer?.id
            return activeId != null && activeId === this.player.id
        },
        lastThrows() {
            return this.matchStore.getCompetitorLastThrows(this.player.team)
        },
        average() {
            return this.matchStore.getCompetitorAverage(this.player.team)
        },
        averageLabel() {
            return this.isCricketGame
                ? this.$t('play_player.three_turn_marks')
                : this.$t('play_player.three_turn_avg')
        },
        checkoutOptions() {
            if (!this.isX01Game || !this.remainingScore || isNaN(this.remainingScore)) return null
            return checkoutService.getCheckoutMessage(this.remainingScore)
        },
        matchData() {
            return this.matchStore.getMatchData
        },
        showSets() {
            return this.matchData?.settings?.sets_to_win > 1
        },
        playerSetsWon() {
            return this.matchStore.getCompetitorSetsWon(this.player.team)
        },
        playerLegsWon() {
            return this.matchStore.getCompetitorLegsWon(this.player.team)
        },
        dartsThrown() {
            if (!this.isX01Game) return 0

            const sets = this.matchData?.sets || []
            if (!sets.length) return 0

            const currentSet = sets[sets.length - 1]
            if (!currentSet?.legs?.length) return 0

            const currentLeg = currentSet.legs[currentSet.legs.length - 1]
            if (!currentLeg?.turns?.length) return 0

            const competitorTurns = currentLeg.turns.filter(
                (turn) => turn.team === this.player.team,
            )

            return competitorTurns.reduce((total, turn) => {
                const thrown = turn.darts_thrown || 0
                return total + (thrown === -1 ? 0 : thrown)
            }, 0)
        },
    },
    methods: {
        scrollToBottom() {
            if (this.$refs.scrollContainer) {
                this.$refs.scrollContainer.scrollTop = this.$refs.scrollContainer.scrollHeight
            }
        },
        getCheckoutDarts() {
            if (!this.checkoutOptions) return []

            const checkoutMessage = this.checkoutOptions

            // Handle multi-dart checkouts (e.g., "T20 + D20 = 100")
            if (checkoutMessage.includes(' + ')) {
                return checkoutMessage.split(' + ').map((part) => part.split(' = ')[0])
            }

            // Handle single-dart checkouts (e.g., "D6 = 12")
            if (checkoutMessage.includes(' = ')) {
                return [checkoutMessage.split(' = ')[0]]
            }

            return [checkoutMessage]
        },
        handleTurnClick(turn, turnIndex) {
            if (this.isTurnFromBot(turn)) return
            if (!this.canEditThisPlayer) return
            this.matchStore.setEditingTurn(this.player.team, turnIndex, turn)
        },
        isTurnFromBot(turn) {
            if (!turn?.fullTurn) return false
            return this.matchStore.isTurnFromBot(turn.fullTurn)
        },
    },
    watch: {},
    mounted() {
        if (this.isX01Game) {
            this.scrollToBottom()
        }
    },
    updated() {
        if (this.isX01Game) {
            this.scrollToBottom()
        }
    },
    beforeUnmount() {},
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
    width: 2px;
}

.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    background: #2b2b2b;
    border-radius: 0;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #474343;
}

.custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #2b2b2b transparent;
}
</style>
